<template>
  <div class="cmp">
    <!-- Each catalog's own answer, as it streams in. -->
    <ul class="cmp__legs" aria-label="Answers from each catalog">
      <li v-for="(c, i) in catalogs" :key="c.id" class="cmp__leg">
        <CatalogRibbon :tone="c.tone" size="sm" />
        <span class="cmp__leg-name">{{ c.college }} <span class="cmp__leg-year">{{ c.edition }}</span></span>
        <span class="cmp__leg-state">
          <template v-if="!turn.legs[i] || turn.legs[i].loading">
            <span class="cmp__pulse" aria-hidden="true"></span> Answering…
          </template>
          <template v-else-if="turn.legs[i].status === 'answered'">
            Found on {{ pageList(turn.legs[i].pages) }}
          </template>
          <template v-else-if="turn.legs[i].status === 'out_of_scope'">Declined</template>
          <template v-else-if="turn.legs[i].error">Couldn't answer</template>
          <template v-else>Not in this catalog</template>
        </span>
      </li>
    </ul>

    <p v-if="turn.error" class="ds-error text-sm">{{ turn.error }}</p>

    <template v-else-if="!turn.comparison">
      <p v-if="legsDone" class="cmp__waiting text-sm text-mute">
        <span class="cmp__pulse" aria-hidden="true"></span> Lining up the answers…
      </p>
    </template>

    <p v-else-if="turn.comparison.status !== 'compared'" class="cmp__summary">
      {{ turn.comparison.summary }}
    </p>

    <template v-else>
      <p class="cmp__summary">{{ turn.comparison.summary }}</p>

      <div class="cmp__grid-wrap">
        <table class="cmp__grid" :style="{ '--cols': catalogs.length }">
          <thead>
            <tr>
              <th scope="col" class="cmp__corner"><span class="sr-only">Topic</span></th>
              <th v-for="c in catalogs" :key="c.id" scope="col" class="cmp__head">
                <CatalogRibbon :tone="c.tone" />
                <span class="cmp__head-text">
                  <span class="cmp__head-name">{{ c.college }}</span>
                  <span class="cmp__head-year">{{ c.edition }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, r) in turn.comparison.rows" :key="r">
              <th scope="row" class="cmp__label">{{ row.label }}</th>
              <td
                v-for="(cell, i) in row.cells"
                :key="i"
                class="cmp__cell"
                :class="{ 'cmp__cell--empty': cell.text === 'Not stated' }"
              >
                <!-- Stacked layout only: which catalog this cell belongs to. -->
                <span class="cmp__cell-tag">
                  <CatalogRibbon :tone="catalogs[i].tone" size="sm" />
                  {{ catalogs[i].college }} · {{ catalogs[i].edition }}
                </span>
                <span class="cmp__cell-text">{{ cell.text }}</span>
                <span v-if="cell.pages.length" class="cmp__pages">
                  <button
                    v-for="p in cell.pages"
                    :key="p"
                    type="button"
                    class="cmp__page"
                    :class="`cmp__page--tone-${catalogs[i].tone}`"
                    :title="`Open ${catalogs[i].college} at page ${p}`"
                    @click="$emit('goto', { catalogId: catalogs[i].id, page: p })"
                  >
                    p. {{ p }}
                  </button>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <details v-if="answeredLegs.length" class="cmp__full">
      <summary>Each catalog's full answer</summary>
      <section v-for="(c, i) in catalogs" :key="c.id" class="cmp__full-leg">
        <h5 class="cmp__full-name">
          <CatalogRibbon :tone="c.tone" size="sm" /> {{ c.college }} · {{ c.edition }}
        </h5>
        <div
          v-if="turn.legs[i] && turn.legs[i].status === 'answered'"
          class="cmp__full-text"
          v-html="renderAnswer(turn.legs[i].answer)"
        ></div>
        <p v-else class="text-sm text-mute">Not in this catalog.</p>
      </section>
    </details>

    <p v-if="turn.done" class="cmp__meta">
      {{ formatCost(turn.done.costUsd) }} · {{ (turn.done.latencyMs / 1000).toFixed(1) }}s
    </p>
  </div>
</template>

<script>
// One comparison turn in AiChatPanel: per-catalog progress, then the summary
// and topic × catalog grid from /api/compare. Every citation carries its
// catalog's ribbon color and opens that catalog at that page.
import CatalogRibbon from "./CatalogRibbon.vue";
// renderAnswer() escapes everything before adding its own tags (v-html above).
import { renderAnswer } from "@/ai/renderAnswer";

export default {
  name: "CompareAnswer",
  components: { CatalogRibbon },
  props: {
    // { legs: [{ loading, status, answer, pages, error }], comparison, done, error }
    turn: { type: Object, required: true },
    // [{ id, college, edition, tone }], in the same order as turn.legs
    catalogs: { type: Array, required: true },
  },
  emits: ["goto"],
  computed: {
    legsDone() {
      return this.catalogs.every((_, i) => this.turn.legs[i] && !this.turn.legs[i].loading);
    },
    answeredLegs() {
      return this.turn.legs.filter((l) => l && l.status === "answered");
    },
  },
  methods: {
    renderAnswer,
    pageList(pages) {
      const p = pages || [];
      return p.length === 1 ? `p. ${p[0]}` : `pp. ${p.slice(0, 3).join(", ")}${p.length > 3 ? "…" : ""}`;
    },
    formatCost(usd) {
      return usd < 0.01 ? `${(usd * 100).toFixed(2)}¢` : `$${usd.toFixed(3)}`;
    },
  },
};
</script>

<style scoped>
.cmp {
  container-type: inline-size;
  font-size: 0.9rem;
  color: var(--ink);
}
.cmp__legs {
  list-style: none;
  margin: 0 0 var(--space-3);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cmp__leg {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
}
.cmp__leg-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--ink-soft);
}
.cmp__leg-year {
  color: var(--mute);
  font-variant-numeric: tabular-nums;
}
.cmp__leg-state {
  color: var(--mute);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cmp__pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--mute);
  animation: cmp-pulse 1s infinite ease-in-out;
}
@keyframes cmp-pulse {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}
.cmp__waiting {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cmp__summary {
  font-family: var(--font-serif);
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0 0 var(--space-3);
}
.cmp__grid-wrap {
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  background: var(--paper);
}
.cmp__grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.cmp__corner {
  width: 22%;
}
.cmp__head {
  text-align: left;
  vertical-align: top;
  padding: 0 10px 10px;
  border-bottom: 1px solid var(--line);
  font-weight: 400;
}
.cmp__head > .ribbon {
  display: block;
  margin-bottom: 6px;
}
.cmp__head-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cmp__head-name {
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.25;
}
.cmp__head-year {
  font-size: 0.72rem;
  color: var(--mute);
  font-variant-numeric: tabular-nums;
}
.cmp__label {
  text-align: left;
  vertical-align: top;
  padding: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--mute);
  line-height: 1.35;
  /* Labels like "NONRESIDENT/INTERNATIONAL TUITION" must wrap inside the column. */
  overflow-wrap: anywhere;
  hyphens: auto;
}
.cmp__cell {
  vertical-align: top;
  padding: 10px;
  line-height: 1.45;
  font-size: 0.82rem;
}
tbody tr + tr > * {
  border-top: 1px solid var(--line);
}
.cmp__cell--empty .cmp__cell-text {
  color: var(--faint);
  font-style: italic;
}
.cmp__cell-tag {
  display: none;
}
.cmp__pages {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.cmp__page {
  appearance: none;
  font: inherit;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid color-mix(in oklab, var(--tone) 35%, var(--paper));
  background: color-mix(in oklab, var(--tone) 7%, var(--paper));
  color: var(--ink);
  cursor: pointer;
}
.cmp__page:hover {
  background: color-mix(in oklab, var(--tone) 16%, var(--paper));
}
.cmp__page:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}
.cmp__page--tone-0 {
  --tone: var(--brand-purple);
}
.cmp__page--tone-1 {
  --tone: var(--brand-pink);
}
.cmp__page--tone-2 {
  --tone: var(--brand-orange);
}
.cmp__full {
  margin-top: var(--space-3);
  font-size: 0.85rem;
}
.cmp__full summary {
  cursor: pointer;
  color: var(--ink-soft);
  font-size: 0.8rem;
}
.cmp__full-leg {
  margin-top: var(--space-3);
}
.cmp__full-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  margin: 0 0 var(--space-1);
}
.cmp__full-text :deep(p) {
  margin: 0 0 var(--space-2);
}
.cmp__full-text :deep(ul) {
  margin: 0 0 var(--space-2);
  padding-left: 1.1rem;
  list-style: disc;
}
.cmp__meta {
  margin: var(--space-2) 0 0;
  font-size: 0.72rem;
  color: var(--faint);
}

/* Narrow panel: each topic becomes a card, one line per catalog. */
@container (max-width: 520px) {
  .cmp__grid,
  .cmp__grid tbody,
  .cmp__grid tr,
  .cmp__grid th,
  .cmp__grid td {
    display: block;
    width: auto;
  }
  .cmp__grid thead {
    display: none;
  }
  .cmp__grid tbody tr + tr > * {
    border-top: 0;
  }
  .cmp__grid tbody tr + tr {
    border-top: 1px solid var(--line);
  }
  .cmp__label {
    padding-bottom: 2px;
  }
  .cmp__cell {
    padding: 6px 10px;
  }
  .cmp__cell-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    color: var(--mute);
    margin-bottom: 2px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cmp__pulse {
    animation: none;
    opacity: 0.6;
  }
}
</style>
